// Use Web Crypto API for browser compatibility
import { NotificationType } from '~/types/enums/notification.enum';
import {
  type NotifyState,
  type Notification
} from '~/types/models/notification.model';

// UUID generator with fallback for older browsers
const getUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const useNotifyStore = defineStore('Notifications', {
  state: (): NotifyState => {
    return {
      notifications: [],
      notificationsArchive: []
    };
  },
  getters: {
    messageType(): (type: NotificationType) => string {
      return (type: NotificationType) => {
        switch (type) {
          case NotificationType.Info:
            return 'Information: ';
          case NotificationType.Success:
            return 'Success: ';
          case NotificationType.Warning:
            return 'Advarsel: ';
          case NotificationType.Error:
            return 'Fejl: ';
          default:
            return '';
        }
      };
    }
  },
  actions: {
    notify(
      heading: string,
      messageOrError: unknown,
      type: NotificationType,
      autoRemove = true
    ): string {
      let message = '';
      if (messageOrError instanceof Error) message = messageOrError.message;
      if (typeof messageOrError === 'string') message = messageOrError;

      const messageType = this.messageType(type);

      const notificationId = getUUID();

      const notification: Notification = {
        id: notificationId,
        message,
        type,
        notifyTime: Date.now(),
        messageType,
        heading
      };
      this.notifications.push(notification);

      if (autoRemove) {
        setTimeout(this.removeNotification.bind(this), 5000, notification);
      }

      return notification.id; // Return the UUID of the created notification
    },
    removeNotification(notification: Notification) {
      // Archive the notification before removing it
      this.notificationsArchive.push(notification);

      // Remove the notification
      this.notifications = this.notifications.filter(
        (n) => n.id !== notification.id
      );
    },
    updateNotification(id: string, heading: string, message: string) {
      const notificationToUpdate = this.notifications.find((n) => n.id === id);
      if (notificationToUpdate) {
        notificationToUpdate.heading = heading;
        notificationToUpdate.message = message;
      }
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useNotifyStore, import.meta.hot));
}
